<template>
  <span ref="trigger" class="inline-flex" @mouseenter="show" @mouseleave="hide" @focusin="show" @focusout="hide">
    <slot />
  </span>

  <Teleport to="body">
    <div
      v-if="visible && label"
      ref="tooltip"
      class="pointer-events-none fixed z-50 max-w-60 rounded-md border border-cockpit-line bg-cockpit-elevated px-2 py-1 text-xs text-cockpit-text shadow-cockpit"
      :style="tooltipStyle"
      role="tooltip">
      {{ label }}
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  label: String,
  color: {
    type: String,
    default: '#0a1516',
  },
  active: {
    type: Boolean,
    default: false,
  },
  size: String,
})

const trigger = ref(null)
const tooltip = ref(null)
const open = ref(false)
const position = ref({ top: 0, left: 0 })
const visible = computed(() => open.value || props.active)

const tooltipStyle = computed(() => ({
  top: `${position.value.top}px`,
  left: `${position.value.left}px`,
  minWidth: props.size || undefined,
  backgroundColor: props.color,
  transform: 'translate(-100%, -100%)',
}))

const updatePosition = () => {
  if (!trigger.value || !tooltip.value) return

  const triggerRect = trigger.value.getBoundingClientRect()
  const tooltipRect = tooltip.value.getBoundingClientRect()
  const spacing = 8
  const left = Math.max(tooltipRect.width + spacing, Math.min(triggerRect.right, window.innerWidth - spacing))
  const top = Math.max(tooltipRect.height + spacing, triggerRect.top - spacing)

  position.value = { top, left }
}

const show = async () => {
  open.value = true
  await nextTick()
  updatePosition()
}

const hide = () => {
  open.value = false
}

watch(visible, async (isVisible) => {
  if (!isVisible) return
  await nextTick()
  updatePosition()
})

onMounted(async () => {
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', updatePosition, true)
  if (visible.value) {
    await nextTick()
    updatePosition()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', updatePosition, true)
})
</script>
